<script setup lang="ts">
import useApprovalsStore from "@/stores/approvals";
import { STATUS, REVIEW_TYPE } from "@/stores/constants";
import { ElCard } from "element-plus";
import { watch, ref, computed } from "vue";
import { config } from "@/config/envConfig";
import { useI18n } from "vue-i18n";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import { useRoute, type RouteLocationRaw } from "vue-router";
import dayjs from "dayjs";

export interface Props {
  status: STATUS.APPROVE | STATUS.REJECT;
  type: REVIEW_TYPE;
}

const props = defineProps<Props>();

const { t } = useI18n();
const route = useRoute();

const PRODUCT_NAME = config.APP_PRODUCT_NAME;

enum STATE {
  ALREADY_DECLINED,
  ALREADY_APPROVED,
  INITIAL_DECLINE,
  INITIAL_APPROVE,
  LAPSED,
}

const approvalsStore = useApprovalsStore();

const initialReview = ref(false);
const approved = ref(props.status === STATUS.APPROVE);
const lapsed = ref(false);
const approvalText = ref("");
const arrivedAtText = ref("");
const reviewedByText = ref("");
const visitingText = ref("");
const pleaseInformText = ref("");
const linkText = ref("");

const getApprovalState = () => {
  if (lapsed.value) return STATE.LAPSED;
  if (initialReview.value && approved.value) return STATE.INITIAL_APPROVE;
  if (initialReview.value && !approved.value) return STATE.INITIAL_DECLINE;
  if (!initialReview.value && approved.value) return STATE.ALREADY_APPROVED;
  return STATE.ALREADY_DECLINED;
};

const formatDate = (datestring: string | undefined) => {
  if (!datestring) return "";
  return dayjs(datestring).format("MMM DD, h:mma");
};

watch(
  () => approvalsStore.visitorResponse?.data,
  (response) => {
    if (props.type !== REVIEW_TYPE.VISIT) return;

    const name = response?.company
      ? `${response?.name} (${response?.company})`
      : response?.name;

    const datetime = formatDate(response?.date);

    arrivedAtText.value = t("authless.arrivedAt", {
      name: name,
      location: response?.location,
      datetime: datetime,
    });

    linkText.value = t("authless.visit.viewVisit");

    lapsed.value = response?.currentStatus === STATUS.LAPSED;
    initialReview.value = response?.currentStatus === undefined;
    approved.value = initialReview.value
      ? props.status === STATUS.APPROVE
      : response?.currentStatus === STATUS.ALLOW;

    switch (getApprovalState()) {
      case STATE.ALREADY_DECLINED: {
        approvalText.value = t("authless.visit.alreadyDeclined");
        reviewedByText.value = t("authless.declinedBy", {
          name: response?.reviewer,
        });
        break;
      }
      case STATE.ALREADY_APPROVED: {
        approvalText.value = t("authless.visit.alreadyApproved");
        reviewedByText.value = t("authless.approvedBy", {
          name: response?.reviewer,
        });
        visitingText.value = t("authless.visit.visiting", {
          name: response?.host,
        });
        break;
      }
      case STATE.INITIAL_DECLINE: {
        approvalText.value = t("authless.visit.declined");
        pleaseInformText.value = t("authless.visit.pleaseInform");
        break;
      }
      case STATE.INITIAL_APPROVE: {
        approvalText.value = t("authless.visit.approved");
        visitingText.value = t("authless.visit.visiting", {
          name: response?.host,
        });
        break;
      }
      case STATE.LAPSED: {
        approvalText.value = t("authless.visit.approvalLapsed");
        break;
      }
    }
  },
  { immediate: true }
);

watch(
  () => approvalsStore.employeeResponse?.data,
  (response) => {
    if (props.type !== REVIEW_TYPE.EMPLOYEE) return;

    const datetime = formatDate(response?.date);

    arrivedAtText.value = t("authless.arrivedAt", {
      name: response?.name,
      location: response?.location,
      datetime: datetime,
    });

    linkText.value = t("authless.employee.viewEmployee");

    lapsed.value = response?.currentStatus === STATUS.LAPSED;
    initialReview.value = response?.currentStatus === undefined;
    approved.value = initialReview.value
      ? props.status === STATUS.APPROVE
      : response?.currentStatus === STATUS.ALLOW;

    switch (getApprovalState()) {
      case STATE.ALREADY_DECLINED: {
        approvalText.value = t("authless.employee.alreadyDeclined");
        reviewedByText.value = t("authless.declinedBy", {
          name: response?.reviewer,
        });
        break;
      }
      case STATE.ALREADY_APPROVED: {
        approvalText.value = t("authless.employee.alreadyApproved");
        reviewedByText.value = t("authless.approvedBy", {
          name: response?.reviewer,
        });
        break;
      }
      case STATE.INITIAL_DECLINE: {
        approvalText.value = t("authless.employee.declined");
        pleaseInformText.value = t("authless.employee.pleaseInform");
        break;
      }
      case STATE.INITIAL_APPROVE: {
        approvalText.value = t("authless.employee.approved");
        break;
      }
      case STATE.LAPSED: {
        approvalText.value = t("authless.employee.approvalLapsed");
        arrivedAtText.value = "";
        break;
      }
    }
  },
  { immediate: true }
);

const timelineRoute = computed<RouteLocationRaw>(() => {
  switch (props.type) {
    case REVIEW_TYPE.EMPLOYEE:
      return {
        name: "employees-timeline",
        params: { id: route.params.movementPk },
      };
    case REVIEW_TYPE.VISIT:
      return {
        name: "visitors-timeline",
        params: { id: route.params.movementPk },
      };
    default:
      return {};
  }
});
</script>

<template>
  <div class="layout">
    <el-card class="card">
      <div class="img-content">
        <img
          :alt="PRODUCT_NAME"
          src="@theme/branding/brand_primary_logo.svg"
          class="brand-logo"
        />
      </div>
      <div class="approval-status">
        <FontAwesomeIcon
          :class="['icon', approved ? 'approved-icon' : 'declined-icon']"
          :icon="['fal', approved ? 'check-circle' : 'circle-xmark']"
        />
        <h2>{{ approvalText }}</h2>
      </div>
      <div class="approval-message">
        <p>{{ arrivedAtText }}</p>
        <p v-if="visitingText">
          {{ visitingText }}
        </p>
        <p
          v-if="reviewedByText"
          :class="approved ? 'approved-by-text' : 'declined-by-text'"
        >
          {{ reviewedByText }}
        </p>
        <p
          v-if="pleaseInformText"
          class="please-inform-text"
        >
          {{ pleaseInformText }}
        </p>
        <div class="link">
          <CoreLink
            :underline="false"
            :route="timelineRoute"
          >
            {{ linkText }}
          </CoreLink>
        </div>
      </div>
    </el-card>
  </div>
</template>

<style lang="scss" scoped>
body {
  background-color: var(--so-authless-background-color);
}

.layout {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 650px;
  min-height: 400px;
}

.card {
  width: 590px;
  height: 300px;
  padding: 2rem;
  border-radius: 0.5rem;
}

.card :deep(div.so-card__body) {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.img-content {
  display: flex;
  height: 20%;
}

.approval-status {
  display: flex;
  align-items: center;
  height: 30%;
  padding-top: 0.5rem;
}

.approval-message {
  display: flex;
  flex-direction: column;
  height: 40%;
}

.approval-status > h2 {
  margin-left: 0.5rem;
}

.approved-by-text {
  color: var(--so-color-success);
  font-weight: var(--font-bold);
}

.declined-by-text {
  color: var(--so-color-error);
  font-weight: var(--font-bold);
}

.icon {
  height: 2rem;
  width: 2rem;
}

.approved-icon {
  color: var(--so-color-success);
}

.declined-icon {
  color: var(--so-color-error);
}

.please-inform-text {
  font-weight: var(--font-bold);
}

.spacer {
  flex-grow: 1;
}

.link {
  margin-top: 0.5rem;
}

.link :deep(.so-link):not(:hover) {
  color: var(--so-color-secondary);
}
</style>
